import React, { useCallback, useRef, useState } from "react";
import {
  emailPlaceholderSelectGroups,
  placeholderTokenMarkup,
} from "./emailTriggerPlaceholders";

function insertAtSelection(el, value, token, onChange) {
  const markup = placeholderTokenMarkup(token);
  if (!el) {
    onChange(value + markup);
    return;
  }
  const start = typeof el.selectionStart === "number" ? el.selectionStart : value.length;
  const end = typeof el.selectionEnd === "number" ? el.selectionEnd : start;
  const next = value.slice(0, start) + markup + value.slice(end);
  onChange(next);
  const caret = start + markup.length;
  requestAnimationFrame(() => {
    el.focus();
    try {
      el.setSelectionRange(caret, caret);
    } catch {
      /* some inputs omit selection APIs */
    }
  });
}

/**
 * Subject line or body editor with "Insert field" at the text cursor.
 */
export default function EmailTemplateFieldInput({
  label,
  value,
  onChange,
  multiline = false,
  placeholder,
  rows = 10,
  inputClassName = "",
}) {
  const inputRef = useRef(null);
  const [pickerKey, setPickerKey] = useState(0);
  const groups = emailPlaceholderSelectGroups();

  const onPickField = useCallback(
    (e) => {
      const token = e.target.value;
      e.target.value = "";
      setPickerKey((k) => k + 1);
      if (!token) return;
      insertAtSelection(inputRef.current, value, token, onChange);
    },
    [value, onChange]
  );

  const controlProps = {
    ref: inputRef,
    value,
    onChange: (e) => onChange(e.target.value),
    placeholder,
    className: inputClassName,
  };

  const headStyle = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  };
  const insertWrapStyle = { display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 };
  const insertLabelStyle = { fontSize: 11, fontWeight: 500, color: "#64748b" };
  const selectStyle = {
    maxWidth: "14rem",
    padding: "4px 8px",
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    fontSize: 12,
    fontFamily: "inherit",
    background: "#fff",
    color: "#334155",
  };

  return (
    <label className="email-triggers-field">
      <span style={headStyle}>
        <span className="email-triggers-field-label">{label}</span>
        <span style={insertWrapStyle}>
          <label htmlFor={`email-insert-${label}`} style={insertLabelStyle}>
            Insert field
          </label>
          <select
            key={pickerKey}
            id={`email-insert-${label}`}
            style={selectStyle}
            defaultValue=""
            onChange={onPickField}
            aria-label={`Insert field into ${label}`}
          >
            <option value="">Choose…</option>
            {groups.map(({ group, fields }) => (
              <optgroup key={group} label={group}>
                {fields.map((f) => (
                  <option key={f.token} value={f.token}>
                    {f.label} ({placeholderTokenMarkup(f.token)})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </span>
      </span>
      {multiline ? (
        <textarea
          {...controlProps}
          rows={rows}
          className={inputClassName ? `email-triggers-textarea ${inputClassName}` : "email-triggers-textarea"}
        />
      ) : (
        <input
          type="text"
          {...controlProps}
          className={inputClassName || "email-triggers-input email-triggers-input-wide"}
        />
      )}
    </label>
  );
}
